import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { IAward } from "../../../../../../interfaces";
import { EmitError } from "../../../../../../utils/EmitError";
import AwardsService from "../../../../../../services/http/services/AwardsService";

export interface ITableData extends IAward {
    key: number | undefined;
    app_user_id: number;
    app_user_cpf: string;
    app_user_name: string;
    app_user_phone: string;
}

export default function useGiveawayAwards() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [isConfirmValidateAwardModalVisible, setIsConfirmValidateAwardModalVisible] = useState<boolean>(false);
    const [invalidating, setInvalidating] = useState<boolean>(false);
    const [selectedAward, setSelectedAward] = useState<IAward | undefined>();

    const { id } = useParams();
    const navigate = useNavigate();

    const onClickViewAppUser = useCallback((appUserId: number) => {
        return navigate('/usuarios/aplicativo/visualizar/' + appUserId);
    }, [navigate]);

    const setTableDataFromRequest = useCallback((data: IAward[]) => {
        if (!data) {
            return;
        }

        const newData = data.map((award) => ({
            ...award,
            key: award.id,
            app_user_id: award.app_user?.id!,
            app_user_name: award?.app_user?.name!,
            app_user_cpf: award.app_user?.cpf!,
            app_user_phone: award.app_user?.phone!,
        }));
        setTableData(newData);
    }, []);

    const loadAwards = useCallback(async (signal?: AbortSignal) => {
        try {
            setIsLoading(true);

            const awardData = await AwardsService.listAwardsByGiveawayId(Number(id), signal);
            setTableDataFromRequest(awardData);
            setIsLoading(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'giveawayAwards_aosdpasdpasd');
        } finally {
            setIsLoading(false);
        }
    }, [id, setTableDataFromRequest]);



    useEffect(() => {
        const controller = new AbortController();

        loadAwards(controller.signal);

        return () => {
            controller.abort();
        }
    }, [setTableDataFromRequest, loadAwards, navigate, id]);

    const handleClickValidateAward = useCallback((award: IAward, isValid: boolean) => {
        setSelectedAward(award);
        if (!isValid) {
            setInvalidating(true);
        }
        setIsConfirmValidateAwardModalVisible(true);
    }, []);


    const handleValidateFinished = useCallback((award: IAward) => {
        const controller = new AbortController();
        loadAwards(controller.signal);

        setIsConfirmValidateAwardModalVisible(false);
        setSelectedAward(undefined);
        setInvalidating(false);
    }, [loadAwards]);

    const handleCancelValidateAward = useCallback(() => {
        setIsConfirmValidateAwardModalVisible(false);
        setInvalidating(false);
    }, []);

    return {
        tableData,
        invalidating,
        isConfirmValidateAwardModalVisible,
        handleValidateFinished,
        handleCancelValidateAward,
        selectedAward,
        onClickViewAppUser,
        handleClickValidateAward,
        isLoading
    }
}
